<script setup lang="ts">
import type { LimitedEvent } from "mws-ts-v2-sdk";

import { useLocalizedDateTime } from "~/composables/useLocalizedDateTime";
import type { ExtendedEventsOverview } from "~/types/Catalog";

const { event } = defineProps<{
  event: LimitedEvent | ExtendedEventsOverview;
  showFinishedText?: boolean;
}>();

const endDate = computed(() => event.endDate);
const { localizedDateTime: localizedEndDate } = useLocalizedDateTime(
  endDate,
  "dateTime",
);

const startDate = computed(() => event.startDate);
const { localizedDateTime: lozalizedStartDate } = useLocalizedDateTime(
  startDate,
  "dateTime",
);

const eventStatus = computed(() => {
  if (event.isFinished) return "finished";

  if (new Date(event.startDate) > new Date()) {
    return "upcoming";
  }

  return "live";
});
</script>

<template>
  <!-- event is live -->
  <div
    v-if="eventStatus === 'live'"
    class="event-card__event-date"
    data-test="event-card-live"
  >
    <BaseCountdownTimer
      :end-date="event.endDate"
      :finished="false"
      :use-color-coding="true"
    />
  </div>

  <!-- event is upcoming -->
  <div
    v-if="eventStatus === 'upcoming'"
    class="event-card__event-date"
    data-test="event-card-upcoming"
  >
    {{ lozalizedStartDate }}
  </div>

  <!-- event is finished -->
  <div
    v-if="eventStatus === 'finished' && showFinishedText"
    class="event-card__event-date text-platform-text-2 uppercase"
  >
    <I18nT
      scope="global"
      keypath="searchResults.eventCardFinishedDate"
      tag="p"
      data-test="event-card-finished-text"
    >
      <template #PLACEHOLDER_date>
        <span class="search-page__header--search-term">
          {{ localizedEndDate }}
        </span>
      </template>
    </I18nT>
  </div>
  <div
    v-else-if="eventStatus === 'finished'"
    class="event-card__event-date"
    data-test="event-card-finished"
  >
    {{ localizedEndDate }}
  </div>
</template>

<style scoped lang="postcss">
.event-card__event-date {
  @apply text-xs;
}
</style>
